import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Activity, ShieldCheck, BarChart3, Cpu, HeartPulse } from 'lucide-react';

// 🧩 Logos (light + dark)
import lumodxLogoLight from '../assets/lumodx-logo.png';
import lumodxLogoDark from '../assets/lumodx-logo-dark.png';
import lumosyeLogoLight from '../assets/Lumosye-logo.png';
import lumosyeLogoDark from '../assets/Lumosye-logo-dark.png';

export default function LumoDxLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return JSON.parse(saved);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // ✅ Sync global dark mode
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      const dark = root.classList.contains('dark');
      setIsDark(dark);
      localStorage.setItem('darkMode', JSON.stringify(dark));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    setIsDark(root.classList.contains('dark'));
    return () => observer.disconnect();
  }, []);

  // 🌗 Toggle function
  const toggleDarkMode = () => {
    const root = document.documentElement;
    root.classList.toggle('dark');
    const dark = root.classList.contains('dark');
    setIsDark(dark);
    localStorage.setItem('darkMode', JSON.stringify(dark));
  };

  const lumodxLogo = isDark ? lumodxLogoDark : lumodxLogoLight;
  const lumosyeLogo = isDark ? lumosyeLogoDark : lumosyeLogoLight;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-white dark:bg-black text-gray-900 dark:text-white min-h-screen transition-colors duration-300">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/80 dark:bg-zinc-900/80 backdrop-blur-lg shadow-md'
            : 'bg-transparent'
        }`}
      >
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/lumodx" className="flex items-center space-x-2">
            <img
              src={lumodxLogo}
              alt="LumoDx Logo"
              className="h-14 md:h-16 w-auto object-contain drop-shadow-md"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link
              to="/"
              className="text-gray-600 dark:text-gray-300 hover:text-lumodx-red transition"
            >
              ← Back to Lumosye Health
            </Link>
            <Link
              to="/login"
              className="text-gray-600 dark:text-gray-300 hover:text-lumodx-red transition"
            >
              Log In
            </Link>

            {/* 🌙 Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="ml-4 text-sm bg-slate-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-100 px-3 py-1.5 rounded-full hover:scale-105 transition"
            >
              {isDark ? '☀️ Light' : '🌙 Dark'}
            </button>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="text-center px-6 py-28 bg-[#f9fafb] dark:bg-[#0a0a0a] relative overflow-hidden border-b border-gray-200 dark:border-zinc-800">
        <div className="absolute inset-0 flex justify-center pointer-events-none">
          <div className="w-[35rem] h-[35rem] bg-lumodx-blue/10 blur-[120px] rounded-full animate-pulse-slow mt-20" />
        </div>

        <div className="relative z-10 container mx-auto">
          <img
            src={lumodxLogo}
            alt="LumoDx Logo"
            className="h-28 md:h-36 mx-auto mb-10 object-contain drop-shadow-xl"
          />

          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight leading-tight">
            Diagnostic Intelligence, <br className="hidden md:block" />
            <span className="text-lumodx-red">Redefined.</span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            LumoDx integrates imaging, waveform, and clinical data to help clinicians uncover
            patterns that matter most — bringing AI-powered clarity to complex diagnostics.
          </p>

          <div className="mt-10 flex justify-center space-x-4">
            <Link
              to="/login"
              className="bg-lumodx-red hover:bg-lumodx-redDark text-white font-semibold px-8 py-3 rounded-full transition shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Early Access
            </Link>
            <a
              href="#features"
              className="bg-lumodx-blue hover:bg-lumodx-blueDark text-white font-semibold px-8 py-3 rounded-full transition shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 bg-[#0f0f0f] text-white transition-colors duration-300">
        <div className="container mx-auto px-6 text-center max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-16">
            Powered by AI. Guided by Clinicians.
          </h2>

          <div className="grid md:grid-cols-3 gap-12">
            <FeatureCard
              icon={Brain}
              title="Imaging Insight"
              text="Vision AI models analyze X-rays, CTs, and MRIs with clinician-level reasoning — augmenting, not replacing, the expert eye."
            />
            <FeatureCard
              icon={Activity}
              title="Signal Analytics"
              text="Advanced waveform detection for ECG, EEG, and POCUS signals, offering pattern alerts and precision metrics in real time."
            />
            <FeatureCard
              icon={Cpu}
              title="Real-Time Computation"
              text="Runs on secure, HIPAA-aligned cloud infrastructure optimized for heavy imaging workloads and low latency inference."
            />
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="py-24 bg-white dark:bg-zinc-900 text-gray-800 dark:text-gray-200 border-t border-gray-100 dark:border-zinc-800 transition-colors duration-300">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-12 max-w-6xl">
          <div className="md:w-1/2 text-left">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Built for Precision. Secured by Design.
            </h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              Every interaction — from image upload to AI inference — occurs within a zero-trust,
              encrypted framework. LumoDx upholds HIPAA compliance and full PHI minimization
              protocols.
            </p>
          </div>
          <div className="flex space-x-10 text-lumodx-blue dark:text-lumodx-red">
            <ShieldCheck size={64} strokeWidth={1.5} />
            <BarChart3 size={64} strokeWidth={1.5} />
            <HeartPulse size={64} strokeWidth={1.5} />
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-28 bg-gradient-to-b from-lumodx-blue/5 to-white dark:from-zinc-900 dark:to-lumodx-red/10 text-center transition-colors duration-300">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Currently in Clinical Pilot
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-400 mb-10 leading-relaxed">
            LumoDx is now undergoing pilot testing with hospital partners to validate its
            AI-assisted diagnostic accuracy. Sign up below to be notified when the public beta
            becomes available.
          </p>
          <a
            href="mailto:contact@lumosyehealth.com?subject=LumoDx%20Pilot%20Interest"
            className="inline-block bg-lumodx-red text-white font-semibold px-10 py-3 rounded-full hover:bg-lumodx-redDark transition shadow-lg hover:shadow-xl"
          >
            Request Pilot Access
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#f3f5f7] dark:bg-zinc-950 text-gray-700 dark:text-gray-300 py-12 text-center border-t border-gray-300 dark:border-zinc-800 transition-colors duration-300">
        <div className="container mx-auto flex flex-col items-center space-y-4">
          <img src={lumosyeLogo} alt="Lumosye Logo" className="h-10 md:h-12 object-contain" />
          <p className="font-medium text-gray-700 dark:text-gray-400">
            © {new Date().getFullYear()} Lumosye Health Services, LLC
          </p>
        </div>
      </footer>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, text }) {
  return (
    <div className="bg-white/5 dark:bg-zinc-800 rounded-2xl p-8 shadow-sm hover:shadow-xl border border-gray-100/10 dark:border-zinc-700 transition transform hover:-translate-y-2">
      <div className="bg-lumodx-red/20 text-lumodx-red dark:text-lumodx-blue rounded-lg h-12 w-12 flex items-center justify-center mb-4">
        <Icon size={24} />
      </div>
      <h3 className="text-xl font-bold mb-2 text-white">{title}</h3>
      <p className="text-gray-300 dark:text-gray-400">{text}</p>
    </div>
  );
}
